import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  NgZone,
  computed,
  inject,
  signal,
} from '@angular/core'

import { PopoverModule } from 'primeng/popover'

import { FlxButtonModule, FlxFormControlModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import { WorkspaceType } from '@flowx/designer/modules/workspaces/shared/commons.types'
import { WorkspaceEnvTypeTagComponent } from '@flowx/designer/modules/workspaces/shared/workspace-env-type-tag/workspace-env-type-tag.component'
import { noResultsCaption } from '@flowx/designer/shared/components/empty-infobox/empty-state-pattern'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'

import { FlxNotificationCenterItemComponent } from './notification-center-item/notification-center-item.component'
import {
  NOTIFICATION_RETENTION_DAYS,
  NotificationFilter,
  NotificationFilterMode,
  groupNotificationsByDate,
} from './notifications-center.model'
import { NotificationsCenterStore } from './notifications-center.store'

import { ExtendedNotification } from '../notifications.model'
import { NotificationsService } from '../services/notifications.service'

/** The minute is the finest unit `relativeAgeLabel` renders, so a faster tick repaints identical text. */
const AGE_REFRESH_INTERVAL_MS = 60_000

/** An open panel renders every arrival itself, so the toast stack stays quiet while it is up. */
const TOAST_SUPPRESSOR = 'notifications-center'

/**
 * The notification bell and the panel it opens. Self-contained on purpose — it reads the root store
 * and nothing else, so moving it out of the application banner and into the global chrome is a
 * one-line change at the mount point.
 */
@Component({
  selector: 'flx-notifications-center',
  imports: [
    PopoverModule,
    FlxButtonModule,
    FlxFormControlModule,
    FlxIconModule,
    FlxSpinnerModule,
    FlxNotificationCenterItemComponent,
    WorkspaceEnvTypeTagComponent,
  ],
  templateUrl: './notifications-center.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxNotificationsCenterComponent {
  #zone = inject(NgZone)
  #notificationsService = inject(NotificationsService)
  #store = inject(NotificationsCenterStore)

  #ticker?: ReturnType<typeof setInterval>

  NOTIFICATION_RETENTION_DAYS = NOTIFICATION_RETENTION_DAYS
  NotificationFilterMode = NotificationFilterMode
  WORKSPACE_TYPES = Object.values(WorkspaceType)

  filter = this.#store.filter
  isFiltering = this.#store.isFiltering
  loading = this.#store.loading
  search = this.#store.search
  unreadCount = this.#store.unreadCount

  currentTime = signal(Date.now())
  filterOpen = signal(false)

  groups = computed(() => groupNotificationsByDate(this.#store.filtered(), this.currentTime()))

  /**
   * Order is load-bearing: `isFiltering` is true for `UNREAD` as well, so that branch must come
   * first, and an active filter outranks an active search by product decision.
   */
  emptyMessage = computed(() => {
    if (this.#store.notifications().length === 0) {
      return 'No notifications yet.'
    }

    if (this.#store.filter() === NotificationFilterMode.UNREAD) {
      return 'No unread notifications.'
    }

    if (this.#store.isFiltering()) {
      return 'No notifications for this filter.'
    }

    return noResultsCaption('notifications')
  })

  constructor() {
    // `p-popover` has no `ngOnDestroy`, so a component destroyed with the panel still open never
    // emits `onHide` — that would leave the ticker running and the suppressor in place, muting the
    // toast stack for the rest of the session.
    inject(DestroyRef).onDestroy(() => {
      this.#stopTicking()
      this.#notificationsService.removeToastSuppressor(TOAST_SUPPRESSOR)
    })
  }

  onPanelShow(): void {
    this.#notificationsService.addToastSuppressor(TOAST_SUPPRESSOR)
    this.#notificationsService.dismissAll()
    this.filterOpen.set(false)
    this.currentTime.set(Date.now())
    this.#stopTicking() // A reopen that interrupts the closing animation skips `onHide`, so never assume it has run.

    this.#ticker = this.#zone.runOutsideAngular(() =>
      setInterval(() => this.currentTime.set(Date.now()), AGE_REFRESH_INTERVAL_MS)
    )
  }

  onPanelHide(): void {
    this.#notificationsService.removeToastSuppressor(TOAST_SUPPRESSOR)
    this.#stopTicking()
  }

  onSearchInput(event: Event): void {
    this.#store.setSearch((event.target as HTMLInputElement).value)
  }

  selectFilter(filter: NotificationFilter): void {
    this.#store.setFilter(filter)
    this.filterOpen.set(false)
  }

  markAsRead(notificationId: string): void {
    void this.#store.markAsRead(notificationId)
  }

  markAllAsRead(): void {
    void this.#store.markAllAsRead()
  }

  /** Clicking a notification's action counts as reading it, just as clicking a toast does. */
  onActionClicked(notification: ExtendedNotification): void {
    if (!notification.markedRead) {
      this.markAsRead(notification.id)
    }
  }

  #stopTicking(): void {
    clearInterval(this.#ticker)
    this.#ticker = undefined
  }
}
